'use client';

import { useUser } from '@auth0/nextjs-auth0/client';
import { TrashIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import React from 'react';

import LoadingSpinner from '@/components/loading-spinner';
import { classNames, distanceToNow } from '@/shared/lib';

import useComments from '../model';

export default function CommentList({ locatedFor }: { locatedFor: 'post' | 'publication' }) {
  const { comments, onDelete, isLoading } = useComments({ locatedFor });
  const { user } = useUser();

  if (isLoading) return <LoadingSpinner />;
  if (!comments?.length) return null;

  return (
    <div className="max-h-[600px] space-y-6 overflow-auto rounded-2xl bg-white p-4 ring-1 ring-border-light dark:bg-slate-700 dark:ring-0">
      {!!comments.length &&
        comments.map((comment) => {
          const { author } = comment;
          const isAuthor = user && user.email === comment.author.email;
          const isAdmin = user && user.email === process.env.NEXT_PUBLIC_AUTH0_ADMIN_EMAIL;

          return (
            <div key={new Date(comment.createdAt).getMilliseconds()} className="flex space-x-4">
              <div className="flex-shrink-0">
                <Image
                  src={author.image ?? ''}
                  alt={author.name ?? 'alt'}
                  width={40}
                  height={40}
                  className="rounded-full"
                />
              </div>

              <div className="flex-grow text-xs md:text-base">
                <div className="flex justify-between space-x-2">
                  <p className="text-left">{comment.author.name}</p>
                  <div className="flex items-center gap-4 md:gap-4">
                    <time className="text-gray-400">{distanceToNow(comment.createdAt)}</time>
                    {(isAdmin || isAuthor) && (
                      <button
                        className="rounded-full p-1 text-gray-400 ring-2 ring-gray-400 hover:text-red-500 hover:ring-red-500"
                        onClick={() => onDelete(comment)}
                        aria-label="Close"
                      >
                        <TrashIcon width={16} height={16} />
                      </button>
                    )}
                  </div>
                </div>
                <div className={classNames(isAuthor ? 'justify-end' : 'justify-start', 'flex')}>
                  <div
                    className={classNames(
                      isAuthor
                        ? 'bg-slate-500 text-right text-white dark:bg-slate-500'
                        : 'text-left',
                      'col-span-1 mt-4 block rounded-lg bg-gray-100 px-2 py-1 text-sm dark:bg-gray-600 md:px-4 md:py-2'
                    )}
                  >
                    {comment.text}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}
